import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileSignature, CheckCircle2, ShieldCheck } from "lucide-react";

// Feature 20 — Tenant Attestation (owner view). Free. Renders only for a sealed stage.
export function TenantAttestation({ propertyId, isLocked, manifestHash, onChanged }) {
  const [ctx, setCtx] = useState(null);
  const [name, setName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const actionIdRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/tenant-attestation`);
      setCtx(data);
    } catch { setCtx(null); }
  }, [propertyId]);

  useEffect(() => { if (isLocked) load(); }, [isLocked, manifestHash, load]);

  if (!isLocked || !ctx || !ctx.is_sealed) return null;

  const current = ctx.current;
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  const sign = async () => {
    if (!name.trim()) { toast.error("Please type your name to sign."); return; }
    if (!confirmed) { toast.error("Please confirm you reviewed the statement and evidence."); return; }
    // Stable action id for THIS attempt: reused verbatim on retry, so lost-response
    // retries and double-clicks create exactly one record. Cleared only on success.
    if (!actionIdRef.current) {
      actionIdRef.current = (window.crypto?.randomUUID?.() || `act_${Date.now()}_${Math.random().toString(16).slice(2)}`);
    }
    setSigning(true);
    try {
      await api.post(`/properties/${propertyId}/tenant-attestation`, {
        signer_name: name.trim(),
        confirmed: true,
        supersedes_attestation_id: correcting && current ? current.attestation_id : null,
        client_action_id: actionIdRef.current,
      });
      toast.success("Attestation signed");
      actionIdRef.current = null;
      setName(""); setConfirmed(false); setCorrecting(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign attestation");
    }
    setSigning(false);
  };

  const showForm = ctx.can_sign && (!current || correcting);
  const outside = ctx.outside_seal_evidence_types || [];

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="tenant-attestation-card">
      <h3 className="font-heading text-lg font-semibold text-navy flex items-center gap-2">
        <FileSignature size={18} /> Tenant attestation
      </h3>

      {current && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="tenant-attestation-signed">
          <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium">
            <CheckCircle2 size={16} /> Signed by {current.signer_name}
            {!current.is_current && (
              <span className="ml-2 rounded-full bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5" data-testid="tenant-attestation-superseded">
                Bound to an earlier seal
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-navy">&ldquo;{current.statement_text}&rdquo;</p>
          <div className="mt-2 text-xs text-[#64748B] space-y-0.5">
            <div>Signed: {fmt(current.signed_at)} · Statement {current.statement_version}</div>
            <div>Covers evidence types: {(current.covered_evidence_types || []).join(", ") || "—"}</div>
            <div className="flex items-center gap-1"><ShieldCheck size={12} /> Bound seal fingerprint: {(current.seal_fingerprint || "").slice(0, 16)}…</div>
          </div>
          <p className="mt-2 text-[11px] text-[#94A3B8]">
            This is your own attestation — not notarization, identity verification, landlord agreement, or proof the underlying events are true.
          </p>
          {!correcting && (
            <button className="mt-3 text-xs font-medium text-blue-600 hover:underline" data-testid="tenant-attestation-correct-btn"
              onClick={() => setCorrecting(true)}>
              Sign a corrected attestation
            </button>
          )}
        </div>
      )}

      {showForm && (
        <div className="mt-4" data-testid="tenant-attestation-form">
          <p className="text-sm text-navy">&ldquo;{ctx.statement_text}&rdquo;</p>
          <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-xs text-[#475569] space-y-1" data-testid="tenant-attestation-seal-context">
            <div>Stage: <span className="font-medium text-navy">{ctx.stage}</span></div>
            <div className="flex items-center gap-1"><ShieldCheck size={12} /> Seal fingerprint: <span className="font-mono break-all">{(ctx.seal_fingerprint || "").slice(0, 24)}…</span></div>
            <div>Sealed: <span className="font-medium">{ctx.sealed_at ? fmt(ctx.sealed_at) : "—"}</span></div>
            <div data-testid="tenant-attestation-covered">Covers evidence types: <span className="font-medium text-navy">{(ctx.covered_evidence_types || []).join(", ") || "—"}</span></div>
            {outside.length > 0 && (
              <div data-testid="tenant-attestation-outside" className="text-[#94A3B8]">Not covered by this seal: {outside.join(", ")}</div>
            )}
          </div>
          <p className="mt-2 text-xs text-[#94A3B8]">{ctx.disclosure}</p>
          <Input
            data-testid="tenant-attestation-name"
            className="mt-3"
            placeholder="Type your full name to sign"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="mt-3 flex items-start gap-2 text-sm text-navy cursor-pointer">
            <Checkbox data-testid="tenant-attestation-confirm" checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} />
            <span>I reviewed the statement and the evidence summary above, and I intend this as my signature.</span>
          </label>
          <div className="mt-4 flex gap-2">
            <Button data-testid="tenant-attestation-sign-btn" onClick={sign} disabled={signing || !name.trim() || !confirmed}>
              {signing ? "Signing…" : "Sign tenant attestation"}
            </Button>
            {correcting && (
              <Button variant="ghost" data-testid="tenant-attestation-cancel-btn" onClick={() => { setCorrecting(false); setName(""); setConfirmed(false); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {ctx.is_archived && (
        <p className="mt-4 text-xs text-[#94A3B8]" data-testid="tenant-attestation-archived-note">
          This property is archived. Existing attestations are shown read-only.
        </p>
      )}
    </div>
  );
}
